import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Switch,
  Divider,
  Tooltip,
  CircularProgress,
  MenuItem,
  Select,
  alpha,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import TimerIcon from "@mui/icons-material/Timer";
import useApp from "../../../store/contexts/AppContext";
import SOCKET_EVENTS from "../../../config/SocketEvents";

// ─── Preset wallpapers ───────────────────────────────────────────────────────

const WALLPAPER_PRESETS = [
  { id: "none", label: "None", value: null, preview: "transparent" },
  // Gradients
  { id: "g1", label: "Sunset", value: "linear-gradient(135deg, #f5af19 0%, #f12711 100%)" },
  { id: "g2", label: "Ocean", value: "linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)" },
  { id: "g3", label: "Forest", value: "linear-gradient(135deg, #134e5e 0%, #71b280 100%)" },
  { id: "g4", label: "Lavender", value: "linear-gradient(135deg, #c471ed 0%, #f64f59 100%)" },
  { id: "g5", label: "Midnight", value: "linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)" },
  { id: "g6", label: "Peach", value: "linear-gradient(135deg, #ffb347 0%, #ffcc33 100%)" },
  { id: "g7", label: "Rose", value: "linear-gradient(135deg, #f953c6 0%, #b91d73 100%)" },
  { id: "g8", label: "Arctic", value: "linear-gradient(135deg, #dfe9f3 0%, #ffffff 100%)" },
  // Solid colors
  { id: "s1", label: "Slate", value: "#1e2a38" },
  { id: "s2", label: "Sage", value: "#d4e6d4" },
  { id: "s3", label: "Blush", value: "#fce4ec" },
  { id: "s4", label: "Sand", value: "#f5f0e8" },
  { id: "s5", label: "Charcoal", value: "#2d2d2d" },
  { id: "s6", label: "Sky", value: "#e3f2fd" },
];

const FONT_SIZES = [
  { id: "small", label: "S", px: 13 },
  { id: "normal", label: "M", px: 15 },
  { id: "large", label: "L", px: 18 },
];

// ─── Styled components ────────────────────────────────────────────────────────

const DrawerPaper = styled(Box)(({ theme }) => ({
  width: 320,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
  overflowY: "auto",
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
  position: "sticky",
  top: 0,
  zIndex: 1,
  backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
}));

const Section = styled(Box)({
  padding: "16px 20px",
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: theme.palette.mode === "dark" ? alpha("#fff", 0.4) : alpha("#000", 0.4),
  marginBottom: "12px",
}));

const SettingRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 0",
}));

const SettingLabel = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const FontToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "6px",
}));

const FontButton = styled(Box, { shouldForwardProp: p => p !== "active" })(({ theme, active }) => ({
  width: 36,
  height: 36,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: "13px",
  border: `1.5px solid ${active
    ? theme.palette.primary.main
    : theme.palette.mode === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)"}`,
  color: active
    ? theme.palette.primary.main
    : theme.palette.mode === "dark" ? alpha("#fff", 0.6) : alpha("#000", 0.5),
  backgroundColor: active
    ? alpha(theme.palette.primary.main, 0.1)
    : "transparent",
  transition: "all 0.15s ease",
  userSelect: "none",
}));

const WallpaperGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "8px",
});

const WallpaperSwatch = styled(Box, { shouldForwardProp: p => p !== "selected" && p !== "wallpaper" })(
  ({ wallpaper, selected, theme }) => ({
    width: "100%",
    aspectRatio: "1",
    borderRadius: "10px",
    cursor: "pointer",
    background: wallpaper || (theme.palette.mode === "dark" ? "#2d2d2d" : "#f5f5f5"),
    border: selected
      ? `2px solid ${theme.palette.primary.main}`
      : "2px solid transparent",
    outline: selected ? `2px solid ${alpha(theme.palette.primary.main, 0.3)}` : "none",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.12s ease, border 0.12s ease",
    "&:hover": { transform: "scale(1.06)" },
  })
);

const CustomColorInput = styled("input")({
  position: "absolute",
  opacity: 0,
  width: "100%",
  height: "100%",
  cursor: "pointer",
  border: "none",
});

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const PhotoUploadOverlay = styled(Box)({
  position: "absolute",
  inset: 0,
  borderRadius: "10px",
  background: "rgba(0,0,0,0.55)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  zIndex: 3,
  backdropFilter: "blur(3px)",
});

const SaveDot = styled("span")(({ theme, delay = 0 }) => ({
  display: "inline-block",
  width: 4,
  height: 4,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  animation: `${keyframes`0%,80%,100%{transform:scale(0);opacity:0.3}40%{transform:scale(1);opacity:1}`} 1.2s ${delay}s ease-in-out infinite`,
}));

// ─── Component ────────────────────────────────────────────────────────────────

const RECENT_WALLPAPERS_KEY = "recentUploadedWallpapers";
const MAX_RECENT = 5;

function loadRecentWallpapers() {
  try { return JSON.parse(localStorage.getItem(RECENT_WALLPAPERS_KEY)) || []; } catch { return []; }
}

function saveRecentWallpaper(url) {
  const recent = loadRecentWallpapers().filter(u => u !== url);
  recent.unshift(url);
  localStorage.setItem(RECENT_WALLPAPERS_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

const DISAPPEARING_OPTIONS = [
  { label: "Off", value: 0 },
  { label: "24 hours", value: 86400 },
  { label: "7 days", value: 604800 },
  { label: "30 days", value: 2592000 },
];

export default function ChatSettings({ open, onClose, onGoBack, roomId, otherUserId }) {
  const { setWallpaperApiCall, appState, setDisappearingIntervalApiCall, getDisappearingIntervalApiCall } = useApp();
  const { activeChat } = appState;
  const colorInputRef = useRef(null);

  const prefKey = roomId ? `chatPref_${roomId}` : null;

  const loadPrefs = () => {
    if (!prefKey) return {};
    try { return JSON.parse(localStorage.getItem(prefKey)) || {}; } catch { return {}; }
  };

  const [prefs, setPrefs] = useState(loadPrefs);
  const [savingWallpaper, setSavingWallpaper] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [customColor, setCustomColor] = useState("#ffffff");
  const [recentWallpapers, setRecentWallpapers] = useState(loadRecentWallpapers);
  const [disappearingInterval, setDisappearingInterval] = useState(0);
  const [savingDisappearing, setSavingDisappearing] = useState(false);

  // Reload prefs whenever the room changes
  useEffect(() => {
    setPrefs(loadPrefs());
    setRecentWallpapers(loadRecentWallpapers());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Re-fetch disappearing interval every time the drawer opens so the current status is always fresh
  useEffect(() => {
    if (!open || !roomId) return;
    getDisappearingIntervalApiCall(roomId).then(res => {
      setDisappearingInterval(res.interval ?? 0);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roomId]);

  // Live-sync the interval when the other user changes it (both users share the same setting)
  useEffect(() => {
    if (!roomId) return;
    const handler = (e) => {
      if (e.detail?.roomId?.toString() === roomId?.toString()) {
        setDisappearingInterval(e.detail.interval ?? 0);
      }
    };
    window.addEventListener("iota:disappearing_updated", handler);
    return () => window.removeEventListener("iota:disappearing_updated", handler);
  }, [roomId]);

  const handleDisappearingChange = async (value) => {
    const intervalSec = Number(value);
    setDisappearingInterval(intervalSec);
    setSavingDisappearing(true);
    // Emit via socket so both users get the DISAPPEARING_UPDATED notice instantly
    appState.socket?.emit(SOCKET_EVENTS.SET_DISAPPEARING, {
      roomId,
      interval: intervalSec,
      receiverId: otherUserId,
    });
    setSavingDisappearing(false);
  };

  const savePref = (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    if (prefKey) localStorage.setItem(prefKey, JSON.stringify(next));
  };

  const fontSize = prefs.fontSize || "normal";
  const sound = prefs.sound !== false;
  const readReceipts = prefs.readReceipts !== false;

  const currentWallpaper = activeChat?.chat_wallpaper ?? null;

  const handlePresetWallpaper = async (value) => {
    setSavingWallpaper(true);
    await setWallpaperApiCall(roomId, value);
    setSavingWallpaper(false);
  };

  const handleCustomPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSavingWallpaper(true);
    setUploadingPhoto(true);
    const result = await setWallpaperApiCall(roomId, file);
    setUploadingPhoto(false);
    setSavingWallpaper(false);
    if (result?.url && result.url.startsWith("http")) {
      saveRecentWallpaper(result.url);
      setRecentWallpapers(loadRecentWallpapers());
    }
  };

  const handleCustomColor = async (e) => {
    const hex = e.target.value;
    setCustomColor(hex);
    setSavingWallpaper(true);
    await setWallpaperApiCall(roomId, hex);
    setSavingWallpaper(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { boxShadow: "none", borderLeft: theme => `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}` } }}
    >
      <DrawerPaper>
        {/* Header */}
        <Header>
          <IconButton size="small" onClick={onGoBack || onClose} sx={{ mr: 1 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: "16px", flex: 1 }}>Chat settings</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Header>

        {/* Font size */}
        <Section>
          <SectionTitle>Message font size</SectionTitle>
          <FontToggle>
            {FONT_SIZES.map(({ id, label, px }) => (
              <FontButton key={id} active={fontSize === id} onClick={() => savePref("fontSize", id)}>
                <span style={{ fontSize: px - 2 }}>{label}</span>
              </FontButton>
            ))}
          </FontToggle>
          <Typography variant="caption" sx={{ color: "text.disabled", mt: 1, display: "block" }}>
            Preview: <span style={{ fontSize: FONT_SIZES.find(f => f.id === fontSize)?.px }}>Hello there!</span>
          </Typography>
        </Section>

        <Divider />

        {/* Toggles */}
        <Section>
          <SectionTitle>Preferences</SectionTitle>

          <SettingRow>
            <SettingLabel>
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>Notification sound</Typography>
              <Typography variant="caption" sx={{ color: "text.disabled" }}>Play a sound for new messages</Typography>
            </SettingLabel>
            <Switch size="small" checked={sound} onChange={e => savePref("sound", e.target.checked)} color="primary" />
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>Read receipts</Typography>
              <Typography variant="caption" sx={{ color: "text.disabled" }}>Let them know when you've read</Typography>
            </SettingLabel>
            <Switch size="small" checked={readReceipts} onChange={e => savePref("readReceipts", e.target.checked)} color="primary" />
          </SettingRow>

        </Section>

        <Divider />

        {/* Disappearing messages */}
        <Section>
          <SectionTitle sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <TimerIcon sx={{ fontSize: 16, opacity: 0.7 }} />
            Disappearing messages
            {savingDisappearing && <CircularProgress size={12} sx={{ ml: 0.5 }} />}
          </SectionTitle>
          <SettingRow>
            <SettingLabel>
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>Auto-delete after</Typography>
              <Typography variant="caption" sx={{ color: "text.disabled" }}>
                {disappearingInterval > 0
                  ? "New messages disappear automatically"
                  : "Messages won't auto-delete"}
              </Typography>
            </SettingLabel>
            <Select
              size="small"
              value={disappearingInterval}
              onChange={e => handleDisappearingChange(e.target.value)}
              sx={{ fontSize: "13px", minWidth: 110 }}
            >
              {DISAPPEARING_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px" }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </SettingRow>
        </Section>

        <Divider />

        {/* Wallpaper */}
        <Section>
          <SectionTitle sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            Chat wallpaper
            {savingWallpaper && (
              <Box sx={{ display: "inline-flex", gap: "3px", alignItems: "center", ml: 0.5 }}>
                <SaveDot delay={0} />
                <SaveDot delay={0.2} />
                <SaveDot delay={0.4} />
              </Box>
            )}
          </SectionTitle>
          <WallpaperGrid>
            {WALLPAPER_PRESETS.map(({ id, label, value }) => {
              const isSelected = currentWallpaper === value;
              return (
                <Tooltip key={id} title={label} placement="top" arrow>
                  <WallpaperSwatch
                    wallpaper={value}
                    selected={isSelected}
                    onClick={() => handlePresetWallpaper(value)}
                  >
                    {isSelected && (
                      <CheckIcon sx={{ fontSize: 16, color: value ? "#fff" : "primary.main", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))" }} />
                    )}
                  </WallpaperSwatch>
                </Tooltip>
              );
            })}

            {/* Custom color swatch */}
            <Tooltip title="Custom color" placement="top" arrow>
              <WallpaperSwatch
                wallpaper={`conic-gradient(red, yellow, lime, aqua, blue, magenta, red)`}
                selected={false}
                sx={{ position: "relative", overflow: "hidden" }}
              >
                <CustomColorInput
                  type="color"
                  value={customColor}
                  onChange={handleCustomColor}
                />
              </WallpaperSwatch>
            </Tooltip>

            {/* Custom photo swatch */}
            <Tooltip title="Custom photo" placement="top" arrow>
              <WallpaperSwatch
                wallpaper={null}
                selected={false}
                sx={{ position: "relative", overflow: "hidden", border: theme => `2px dashed ${alpha(theme.palette.primary.main, 0.4)}` }}
              >
                {uploadingPhoto ? (
                  <PhotoUploadOverlay>
                    <CircularProgress size={22} thickness={4} sx={{ color: "#fff" }} />
                    <Typography sx={{ fontSize: "8px", color: "#fff", fontWeight: 700, mt: 0.3, letterSpacing: "0.03em" }}>
                      uploading
                    </Typography>
                  </PhotoUploadOverlay>
                ) : (
                  <Typography sx={{ fontSize: "10px", color: "primary.main", fontWeight: 700, textAlign: "center", lineHeight: 1.2, pointerEvents: "none" }}>
                    📷<br />Photo
                  </Typography>
                )}
                <CustomColorInput
                  type="file"
                  accept="image/*"
                  onChange={handleCustomPhoto}
                  disabled={uploadingPhoto}
                />
              </WallpaperSwatch>
            </Tooltip>
          </WallpaperGrid>

          {/* Recent uploaded photos */}
          {recentWallpapers.length > 0 && (
            <>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.disabled", mt: 2, mb: 1 }}>
                Recent photos
              </Typography>
              <WallpaperGrid>
                {recentWallpapers.map((url, i) => {
                  const isSelected = currentWallpaper === url;
                  return (
                    <Tooltip key={i} title="Reuse photo" placement="top" arrow>
                      <WallpaperSwatch
                        wallpaper={null}
                        selected={isSelected}
                        onClick={() => handlePresetWallpaper(url)}
                        sx={{
                          backgroundImage: `url(${url})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          position: "relative",
                        }}
                      >
                        {isSelected && (
                          <CheckIcon sx={{ fontSize: 16, color: "#fff", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))" }} />
                        )}
                      </WallpaperSwatch>
                    </Tooltip>
                  );
                })}
              </WallpaperGrid>
            </>
          )}
        </Section>
      </DrawerPaper>
    </Drawer>
  );
}
